import React, { useCallback, useEffect, useRef } from "react";
import Styles from "./DynamicBanner.module.css";
import { Banner } from "@reconvert/react-ui-component";
import { Button, Flex, Skeleton } from "@chakra-ui/react";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { selectAppState } from "../../app/slice/appSlice";
import { useAppSelector } from "../../app/hooks";
import { BannerAnalyticsPayload, bannerClick, BannerItem } from "../../app/slice/bannerSlice";
import { countAdminBannerImp } from "../../services/app.service";


interface BannerState {
  isBannerLoading: boolean;
  flowBanners: BannerItem[];
}


interface DynamicBannerProps {
  direction: "top" | "left" | "right";
}


const bannerTypes: Record<string, string> = {
  "DEFAULT": "info",
  "SUCCESS": "success",
  "INFO": "info",
  "WARNING": "warning",
  "ERROR": "critical",
};


const selectBanner = (state: { banner: BannerState }) => state.banner;
const RenderSkeleton = () => <Skeleton height={'113px'} mb="10px" />;
const RenderEmptyContent = () => <></>;

export const bannerDetails = createSelector([selectBanner], banner => {
  return {
    flowBannerData: banner.flowBanners || [],
    loading: banner.isBannerLoading,
  };
});


const DynamicBanner: React.FC<DynamicBannerProps> = ({ direction }) => {
  const {flowBannerData,loading } = useSelector(bannerDetails);
  const bannerData = flowBannerData;
  
  console.log('flowBannerData', flowBannerData);
  if (!flowBannerData || loading) {
    
    return (
      <>
        {direction === 'top' && <RenderSkeleton />}
        {(direction === 'left' || direction === 'right') && (
          <RenderEmptyContent />
        )}
      </>
    );
  }

  return <div>{bannerData?.length > 0 && <BannerLayout key={bannerData[0].id} item={bannerData[0]} direction={direction} />}</div>;
};

interface BannerLayoutProps {
  item: BannerItem;
  direction: "top" | "left" | "right";
}

const BannerLayout: React.FC<BannerLayoutProps> = React.memo(({ item, direction }) => {
  const dispatch = useDispatch();
  const { storeInfo } = useAppSelector(selectAppState);
  const isRender = useRef(false);
   

  useEffect(() => {
    
    const bannerId = item?.id;
    
    if (isRender.current || !bannerId || !storeInfo?.storeUrl) return;

    isRender.current = true;

    if (item.displayRule === "X_IMPRESSIONS_PER_MERCHANT") {
      const getBannersImp =
        localStorage.getItem(
          `bannerImp_${bannerId}_${storeInfo?.storeUrl}`
        ) || 0;

      localStorage.setItem(
        `bannerImp_${bannerId}_${storeInfo?.storeUrl}`,
        (Number(getBannersImp) + 1).toString()
      );
    }

    const payload: BannerAnalyticsPayload = {
      topic: "analytics.shopify.banner.impression",
      data: [
        {
          date: Date.now(),
          value: 1,
          type: "IMPRESSION",
          bannerId,
          store: storeInfo?.storeUrl || '',
          development: storeInfo?.is_partner_development === 1
        }
      ]
    };

    countAdminBannerImp(payload, storeInfo?.storeUrl || '');

  }, [item, storeInfo?.storeUrl]);

  const decodeHTMLEntities = (text: string) => {
    const parser = new DOMParser();

    return parser.parseFromString(`<!doctype html><body>${text}`, "text/html").body.textContent ?? "";
  };

  const handleOnClick = useCallback(
    (isDismiss = false) => {
      dispatch(
        bannerClick({
          item,
          shopName: storeInfo?.storeUrl || '',
          isDismiss,
          isPartnerDevelopment: storeInfo?.is_partner_development === 1
        }),
      );
    },
    [dispatch, storeInfo?.storeUrl, item],
  );

  const bannerType = bannerTypes[item.bannerType];

  const className: any =
    direction === "top" ? null : direction === "left" ? Styles.Left_dynamic_banner : Styles.Right_dynamic_banner;

  return (
    <div key={item.id} className={className} style={{ marginBottom: "10px" }}>
      <Banner
        title={decodeHTMLEntities(item.title)}
        status={bannerType as any}
        onDismiss={
          item.isDismissible ? () => handleOnClick(true) : undefined
        }
        description={
          <Flex gap="8px" direction="column" align="flex-start">
            <p dangerouslySetInnerHTML={{ __html: item.description }}></p>
            <Button
            color="link.color"
              variant={'link'}
              onClick={() => {
                handleOnClick();
                if (item.buttonUrl) window.open(item.buttonUrl, "_blank");
              }}
            >
              {decodeHTMLEntities(item.buttonText)}
            </Button>
          </Flex>
        }
      />
    </div>
  );
});

export default React.memo(DynamicBanner);
